import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ActionsMenu from '../../components/common/ActionsMenu';
import Modal from '../../components/common/Modal';
import {
  ADMIN_ALL_PROJECTS_QUERY,
  ADMIN_ALL_USERS_QUERY,
  ADMIN_PROJECT_MEMBERS_QUERY,
} from '../../graphql/queries';
import {
  ADMIN_UPDATE_PROJECT_OPEN_LOG_LIMIT_MUTATION,
  ADMIN_CHANGE_PROJECT_OWNER_MUTATION,
  ADMIN_REMOVE_PROJECT_MEMBER_MUTATION,
  ADMIN_DELETE_PROJECT_MUTATION,
  ADMIN_RESTORE_PROJECT_MUTATION,
} from '../../graphql/mutations';
import styles from './AdminProjects.module.css';

const formatStorage = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const MAX_OPEN_LOG_LIMIT = 10000;

interface AdminProjectRow {
  id: string;
  name: string;
  location?: string | null;
  status: string;
  thumbnailUrl?: string | null;
  fileCount: number;
  storageUsed: number;
  createdAt: string;
  updatedAt: string;
  openLogLimit: number;
  deletedAt?: string | null;
  openLogs: { email: string; ip: string; at: string }[];
  owner: { id: string; firstName: string; lastName: string; email: string };
}

type ViewMode = 'list' | 'grid';

const STATUS_COLORS: Record<string, string> = {
  active: 'badge-green',
  archived: 'badge-orange',
  completed: 'badge-blue',
};

type OwnerOption = { id: string; firstName: string; lastName: string; email: string };

function OwnerFilter({ value, onChange }: { value: OwnerOption | null; onChange: (o: OwnerOption | null) => void }) {
  const [open, setOpen]         = useState(false);
  const [inputValue, setInput]  = useState('');
  const wrapRef                 = useRef<HTMLDivElement>(null);
  const debounceRef             = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [loadUsers, { data, loading }] = useLazyQuery(ADMIN_ALL_USERS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const users: OwnerOption[] = data?.adminAllUsers?.users || [];

  const triggerLoad = useCallback((search: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadUsers({ variables: { page: 1, limit: 20, search: search || undefined, account: 'main' } });
    }, 220);
  }, [loadUsers]);

  const handleOpen = () => {
    setOpen(true);
    triggerLoad(inputValue);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
    triggerLoad(e.target.value);
    if (value) onChange(null);
  };

  const select = (u: OwnerOption) => {
    onChange(u);
    setInput(`${u.firstName} ${u.lastName}`);
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setInput('');
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={wrapRef} className={styles.ownerFilter}>
      <div className={styles.ownerInputWrap}>
        <span className="material-symbols-outlined text-sm text-on-surface-variant">person</span>
        <input
          className={styles.ownerInput}
          placeholder="Filter by owner…"
          value={inputValue}
          onFocus={handleOpen}
          onChange={handleInput}
        />
        {value && (
          <button onClick={clear} className={styles.ownerClear} title="Clear filter">
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        )}
      </div>

      {open && (
        <div className={styles.ownerDropdown}>
          {loading && (
            <div className={styles.ownerDropdownMsg}>
              <span className="material-symbols-outlined text-sm animate-spin mr-1">refresh</span>
              Loading…
            </div>
          )}
          {!loading && users.length === 0 && (
            <div className={styles.ownerDropdownMsg}>No users found</div>
          )}
          {!loading && users.map((u) => (
            <button key={u.id} className={styles.ownerOption} onMouseDown={() => select(u)}>
              <span className={styles.ownerOptionName}>{u.firstName} {u.lastName}</span>
              <span className={styles.ownerOptionEmail}>{u.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

type PickerUser = OwnerOption & { isActive: boolean; companyName?: string };

/** Inline list rather than a floating dropdown: the modal scrolls its own body,
 *  so an absolutely positioned menu would be clipped by it. */
function OwnerPicker({ excludeId, value, onChange }: {
  excludeId: string;
  value: PickerUser | null;
  onChange: (u: PickerUser) => void;
}) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 220);
    return () => clearTimeout(t);
  }, [search]);

  const { data, loading } = useQuery(ADMIN_ALL_USERS_QUERY, {
    variables: { page: 1, limit: 8, search: query || undefined, account: 'main' },
    fetchPolicy: 'cache-and-network',
  });
  const users: PickerUser[] = (data?.adminAllUsers?.users || [])
    .filter((u: PickerUser) => u.id !== excludeId);

  return (
    <div className={styles.picker}>
      <div className={styles.ownerInputWrap}>
        <span className="material-symbols-outlined text-sm text-on-surface-variant">search</span>
        <input
          className={styles.ownerInput}
          placeholder="Search by name, email or company…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      <div className={styles.pickerList}>
        {loading && users.length === 0 ? (
          <p className={styles.pickerMsg}>Searching…</p>
        ) : users.length === 0 ? (
          <p className={styles.pickerMsg}>No other users match that search.</p>
        ) : (
          users.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => onChange(u)}
              className={`${styles.pickerRow} ${value?.id === u.id ? styles.pickerRowActive : ''}`}
            >
              <span className={styles.ownerOptionName}>
                {u.firstName} {u.lastName}
                {!u.isActive && <span className={styles.inactiveTag}>deactivated</span>}
              </span>
              <span className={styles.ownerOptionEmail}>
                {u.email}{u.companyName ? ` · ${u.companyName}` : ''}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

const AdminProjects = () => {
  const navigate = useNavigate();
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [sortBy, setSortBy]       = useState('updatedAt');
  const [sortDir, setSortDir]     = useState<'asc' | 'desc'>('desc');
  const [ownerFilter, setOwnerFilter] = useState<OwnerOption | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [settingsProject, setSettingsProject] = useState<{ id: string; name: string; openLogLimit: number; openLogs: { email: string; ip: string; at: string }[] } | null>(null);
  const [logLimitInput, setLogLimitInput] = useState('');
  const [logsProject, setLogsProject] = useState<{ name: string; openLogs: { email: string; ip: string; at: string }[] } | null>(null);
  const [ownerProject, setOwnerProject] = useState<{
    id: string; name: string; storageUsed: number;
    owner: { id: string; firstName: string; lastName: string; email: string };
  } | null>(null);
  const [newOwner, setNewOwner] = useState<PickerUser | null>(null);
  const [ignoreStorage, setIgnoreStorage] = useState(false);
  const [keepPrevious, setKeepPrevious] = useState(true);
  const [ownerError, setOwnerError] = useState('');
  const [teamProject, setTeamProject] = useState<{ id: string; name: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [teamError, setTeamError] = useState('');
  const [onlyDeleted, setOnlyDeleted] = useState(false);
  const [deleteProject, setDeleteProject] = useState<{ id: string; name: string } | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [restoreError, setRestoreError] = useState('');
  const [logLimitError, setLogLimitError] = useState('');
  const limit = 20;

  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const [updateOpenLogLimit, { loading: savingLimit }] = useMutation(ADMIN_UPDATE_PROJECT_OPEN_LOG_LIMIT_MUTATION, {
    onCompleted: (data) => {
      setSettingsProject((p) => p ? { ...p, openLogLimit: data.adminUpdateProjectOpenLogLimit.openLogLimit } : p);
    },
    onError: (e) => setLogLimitError(e.message),
  });

  const closeOwnerDialog = () => {
    setOwnerProject(null);
    setNewOwner(null);
    setIgnoreStorage(false);
    setKeepPrevious(true);
    setOwnerError('');
  };

  // Refetched, not just cache-patched: an owner filter or sort can move the row
  // out of the current page entirely.
  const [changeOwner, { loading: changingOwner }] = useMutation(ADMIN_CHANGE_PROJECT_OWNER_MUTATION, {
    refetchQueries: ['AdminAllProjects'],
    onCompleted: closeOwnerDialog,
    onError: (e) => setOwnerError(e.message),
  });

  const openOwnerDialog = (project: {
    id: string; name: string; storageUsed: number;
    owner: { id: string; firstName: string; lastName: string; email: string };
  }) => {
    setNewOwner(null);
    setIgnoreStorage(false);
    setKeepPrevious(true);
    setOwnerError('');
    setOwnerProject(project);
  };

  const saveOwner = () => {
    if (!ownerProject || !newOwner) return;
    setOwnerError('');
    changeOwner({
      variables: {
        projectId: ownerProject.id,
        newOwnerId: newOwner.id,
        ignoreStorageLimit: ignoreStorage,
        keepPreviousOwner: keepPrevious,
      },
    });
  };

  const { data: teamData, loading: teamLoading, refetch: refetchTeam } = useQuery(
    ADMIN_PROJECT_MEMBERS_QUERY,
    { variables: { projectId: teamProject?.id }, skip: !teamProject, fetchPolicy: 'cache-and-network' },
  );
  const team = teamData?.adminProjectMembers;

  // ProjectTeam has no id, so Apollo cannot normalise the mutation result onto
  // the query — refetch instead of hand-patching the cache.
  const [removeMember, { loading: removingMember }] = useMutation(ADMIN_REMOVE_PROJECT_MEMBER_MUTATION, {
    onCompleted: () => { setConfirmRemove(null); refetchTeam(); },
    onError: (e) => { setConfirmRemove(null); setTeamError(e.message); },
  });

  const [softDelete, { loading: deleting }] = useMutation(ADMIN_DELETE_PROJECT_MUTATION, {
    refetchQueries: ['AdminAllProjects'],
    onCompleted: () => setDeleteProject(null),
    onError: (e) => setDeleteError(e.message),
  });

  const [restoreProject, { loading: restoring }] = useMutation(ADMIN_RESTORE_PROJECT_MUTATION, {
    refetchQueries: ['AdminAllProjects'],
    onError: (e) => setRestoreError(`Could not restore the project: ${e.message}`),
  });

  const handleRestore = (projectId: string) => {
    setRestoreError('');
    restoreProject({ variables: { projectId } });
  };

  const closeTeamDialog = () => {
    setTeamProject(null);
    setConfirmRemove(null);
    setTeamError('');
  };

  const openSettings = (project: { id: string; name: string; openLogLimit: number; openLogs: { email: string; ip: string; at: string }[] }) => {
    setSettingsProject(project);
    setLogLimitInput(String(project.openLogLimit));
    setLogLimitError('');
  };

  const saveLogLimit = () => {
    if (!settingsProject) return;
    const trimmed = logLimitInput.trim();
    const val = Number(trimmed);
    if (!/^\d+$/.test(trimmed) || val > MAX_OPEN_LOG_LIMIT) {
      setLogLimitError(`Enter a whole number from 0 to ${MAX_OPEN_LOG_LIMIT}.`);
      return;
    }
    setLogLimitError('');
    updateOpenLogLimit({ variables: { projectId: settingsProject.id, openLogLimit: val } });
  };

  const { data, loading, error } = useQuery(ADMIN_ALL_PROJECTS_QUERY, {
    variables: {
      page, limit,
      search: search || undefined,
      ownerId: ownerFilter?.id || undefined,
      sortBy, sortDir,
      onlyDeleted: onlyDeleted || undefined,
    },
    fetchPolicy: 'cache-and-network',
  });

  const projects: AdminProjectRow[] = data?.adminAllProjects?.projects || [];
  const total = data?.adminAllProjects?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const actionsFor = (project: AdminProjectRow) => (project.deletedAt ? [
    {
      label: 'Restore project', icon: 'restore_from_trash', disabled: restoring,
      hint: 'Restore this project for its owner and members',
      onSelect: () => handleRestore(project.id),
    },
  ] : [
    { label: 'Open project', icon: 'open_in_new', onSelect: () => navigate(`/projects/${project.id}`) },
    { label: 'Open logs', icon: 'history', onSelect: () => setLogsProject(project) },
    { label: 'Team members', icon: 'group', onSelect: () => { setTeamError(''); setConfirmRemove(null); setTeamProject(project); } },
    { label: 'Change owner', icon: 'manage_accounts', onSelect: () => openOwnerDialog(project) },
    { label: 'Project settings', icon: 'settings', onSelect: () => openSettings(project) },
    { label: 'Delete project', icon: 'delete', danger: true, onSelect: () => { setDeleteError(''); setDeleteProject(project); } },
  ]);

  return (
    <DashboardLayout onSearch={(q) => { setSearch(q); setPage(1); }} searchPlaceholder="Search projects...">
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2 text-xs text-on-surface-variant mb-2">
            <button onClick={() => navigate('/admin')} className="hover:text-primary transition-colors">Admin</button>
            <span className="material-symbols-outlined text-sm">chevron_right</span>
            <span>All Projects</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
              <span className="material-symbols-outlined">folder_managed</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-on-surface font-headline">All Projects</h2>
              <p className="text-on-surface-variant text-sm">{total} total projects across the platform</p>
            </div>
          </div>
        </div>

        <div className={styles.filterBar}>
          <OwnerFilter
            value={ownerFilter}
            onChange={(o) => { setOwnerFilter(o); setPage(1); }}
          />
          {ownerFilter && (
            <span className={styles.activeFilterBadge}>
              <span className="material-symbols-outlined text-xs">filter_alt</span>
              {ownerFilter.firstName} {ownerFilter.lastName}
            </span>
          )}
          <label className={styles.deletedToggle}>
            <input
              type="checkbox"
              checked={onlyDeleted}
              onChange={(e) => { setOnlyDeleted(e.target.checked); setPage(1); }}
            />
            <span>Only deleted projects</span>
          </label>
          <div className={styles.viewToggle} role="group" aria-label="Show projects as">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              aria-pressed={viewMode === 'list'}
              aria-label="List view"
              title="List view"
              className={viewMode === 'list' ? styles.viewActive : undefined}
            >
              <span className="material-symbols-outlined text-lg">list</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              aria-pressed={viewMode === 'grid'}
              aria-label="Thumbnail view"
              title="Thumbnail view"
              className={viewMode === 'grid' ? styles.viewActive : undefined}
            >
              <span className="material-symbols-outlined text-lg">grid_view</span>
            </button>
          </div>
        </div>

        {restoreError && <p className={styles.ownerError} role="alert">{restoreError}</p>}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
            Loading projects...
          </div>
        ) : error && projects.length === 0 ? (
          <div className={styles.loadError} role="alert">
            <span className="material-symbols-outlined">error</span>
            Could not load projects: {error.message}
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="material-symbols-outlined text-3xl">folder_managed</span>
            </div>
            <h3 className="text-lg font-bold text-on-surface mb-2">No projects found</h3>
            <p className="text-sm text-on-surface-variant">No projects match your search criteria.</p>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className={styles.cardGrid}>
                {projects.map((project) => (
                  <article key={project.id} className={`${styles.card} ${project.deletedAt ? styles.cardDeleted : ''}`} aria-label={project.name}>
                    {project.deletedAt ? (
                      <div className={styles.cardThumb}>
                        {project.thumbnailUrl
                          ? <img src={project.thumbnailUrl} alt="" />
                          : <span className="material-symbols-outlined">domain</span>}
                      </div>
                    ) : (
                      <button type="button" className={styles.cardThumb} onClick={() => navigate(`/projects/${project.id}`)} aria-label={`Open ${project.name}`}>
                        {project.thumbnailUrl
                          ? <img src={project.thumbnailUrl} alt="" />
                          : <span className="material-symbols-outlined">domain</span>}
                      </button>
                    )}
                    <div className={styles.cardBody}>
                      <div className={styles.cardHead}>
                        <h3 className={styles.cardTitle}>
                          {project.name}
                          {project.deletedAt && (
                            <span className={styles.deletedTag} title={`Deleted ${formatDate(project.deletedAt)}`}>deleted</span>
                          )}
                        </h3>
                        <ActionsMenu label={`Actions for ${project.name}`} actions={actionsFor(project)} />
                      </div>
                      <div className={styles.cardMeta}>
                        <span className={STATUS_COLORS[project.status] || 'badge-orange'}>{project.status}</span>
                        {project.location && (
                          <span className={styles.cardLocation}>
                            <span className="material-symbols-outlined text-xs">location_on</span>
                            {project.location}
                          </span>
                        )}
                      </div>
                      <div className={styles.cardOwner}>
                        <span className={styles.cardOwnerName}>{project.owner.firstName} {project.owner.lastName}</span>
                        <span className={styles.cardOwnerEmail}>{project.owner.email}</span>
                      </div>
                      <div className={styles.cardStats}>
                        <span><span className="material-symbols-outlined text-sm">description</span>{project.fileCount} files</span>
                        <span><span className="material-symbols-outlined text-sm">hard_drive</span>{formatStorage(project.storageUsed)}</span>
                      </div>
                      <div className={styles.cardDates}>
                        <span>Created {formatDate(project.createdAt)}</span>
                        <span>Updated {formatDate(project.updatedAt)}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
            <div className="bg-white rounded-xl overflow-hidden">
              <div className={styles.tableWrap}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-low">
                    <th className="table-header">Project Name</th>
                    <th className="table-header">Owner</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Files</th>
                    <th className="table-header">Storage</th>
                    <th className="table-header">Created</th>
                    <th className="table-header">
                      <button
                        onClick={() => toggleSort('updatedAt')}
                        className="flex items-center gap-1 hover:text-primary transition-colors"
                      >
                        Last updated
                        <span className="material-symbols-outlined text-sm">
                          {sortBy === 'updatedAt' ? (sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward') : 'unfold_more'}
                        </span>
                      </button>
                    </th>
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {projects.map((project) => (
                    <tr key={project.id} className={`table-row group ${project.deletedAt ? styles.deletedRow : ''}`}>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-on-surface">
                            {project.name}
                            {project.deletedAt && (
                              <span className={styles.deletedTag} title={`Deleted ${formatDate(project.deletedAt)}`}>
                                deleted
                              </span>
                            )}
                          </p>
                          {project.location && (
                            <p className="text-xs text-on-surface-variant flex items-center gap-1 mt-0.5">
                              <span className="material-symbols-outlined text-xs">location_on</span>
                              {project.location}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-on-surface">{project.owner.firstName} {project.owner.lastName}</p>
                        <p className="text-xs text-on-surface-variant">{project.owner.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={STATUS_COLORS[project.status] || 'badge-orange'}>{project.status}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-on-surface-variant">{project.fileCount}</td>
                      <td className="px-6 py-4 text-sm text-on-surface-variant">{formatStorage(project.storageUsed)}</td>
                      <td className="px-6 py-4 text-xs text-on-surface-variant">{formatDate(project.createdAt)}</td>
                      <td className="px-6 py-4 text-xs text-on-surface-variant">{formatDate(project.updatedAt)}</td>
                      <td className="px-6 py-4 text-right">
                        <ActionsMenu label={`Actions for ${project.name}`} actions={actionsFor(project)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
            )}

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-outline-variant disabled:opacity-40 hover:bg-surface-container transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-on-surface-variant">
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-outline-variant disabled:opacity-40 hover:bg-surface-container transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <Modal
        isOpen={!!settingsProject}
        onClose={() => setSettingsProject(null)}
        title={settingsProject ? `Settings — ${settingsProject.name}` : ''}
        footer={
          <>
            <button
              onClick={() => setSettingsProject(null)}
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
            >
              Close
            </button>
            <button
              onClick={saveLogLimit}
              disabled={savingLimit}
              className="btn-primary px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container disabled:opacity-50"
            >
              {savingLimit ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {settingsProject && (
          <div className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                Max open logs to keep
              </label>
              <p className="text-xs text-on-surface-variant">
                How many recent project-open events to retain for this project. Set to 0 to disable logging.
              </p>
              <input
                type="number"
                min={0}
                max={MAX_OPEN_LOG_LIMIT}
                className="input-field w-36"
                value={logLimitInput}
                onChange={(e) => { setLogLimitInput(e.target.value); setLogLimitError(''); }}
              />
            </div>
            {logLimitError && <p className={styles.ownerError} role="alert">{logLimitError}</p>}
          </div>
        )}
      </Modal>
      <Modal
        isOpen={!!deleteProject}
        onClose={() => setDeleteProject(null)}
        title={deleteProject ? `Delete ${deleteProject.name}?` : ''}
        footer={
          <>
            <button
              onClick={() => setDeleteProject(null)}
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={deleting}
              onClick={() => {
                if (!deleteProject) return;
                setDeleteError('');
                softDelete({ variables: { projectId: deleteProject.id } });
              }}
              className={styles.dangerBtn}
            >
              {deleting ? 'Deleting…' : 'Delete project'}
            </button>
          </>
        }
      >
        {deleteProject && (
          <div className="space-y-3">
            <p className="text-sm text-on-surface">
              The project disappears for its owner and everyone it is shared with. Nothing is erased:
              its files stay in place, it remains listed here marked as deleted, and you can restore it.
            </p>
            {deleteError && <p className={styles.ownerError}>{deleteError}</p>}
          </div>
        )}
      </Modal>
      <Modal
        isOpen={!!teamProject}
        onClose={closeTeamDialog}
        title={teamProject ? `Team — ${teamProject.name}` : ''}
        footer={
          <button
            onClick={closeTeamDialog}
            className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
          >
            Close
          </button>
        }
      >
        {teamProject && (
          teamLoading && !team ? (
            <div className="flex items-center py-6 text-on-surface-variant">
              <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
              Loading team…
            </div>
          ) : !team ? null : (
            <div className="space-y-3">
              {teamError && <p className={styles.ownerError}>{teamError}</p>}

              <div className={styles.memberRow}>
                <div>
                  <p className="text-sm font-medium text-on-surface">
                    {team.owner.firstName} {team.owner.lastName}
                    <span className={styles.ownerTag}>owner</span>
                  </p>
                  <p className="text-xs text-on-surface-variant">{team.owner.email}</p>
                </div>
                <span className="text-xs text-on-surface-variant">Use “Change owner” to replace</span>
              </div>

              {team.members.length === 0 ? (
                <p className="text-sm text-on-surface-variant py-2">
                  No team members — only the owner has access to this project.
                </p>
              ) : (
                team.members.map((m: {
                  role: string; joinedAt: string;
                  user: { id: string; firstName: string; lastName: string; email: string; companyName?: string };
                }) => (
                  <div key={m.user.id} className={styles.memberRow}>
                    <div>
                      <p className="text-sm font-medium text-on-surface">
                        {m.user.firstName} {m.user.lastName}
                        <span className={styles.roleTag}>{m.role}</span>
                      </p>
                      <p className="text-xs text-on-surface-variant">
                        {m.user.email}{m.user.companyName ? ` · ${m.user.companyName}` : ''}
                        {' · joined '}{formatDate(m.joinedAt)}
                      </p>
                    </div>
                    {confirmRemove === m.user.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setConfirmRemove(null)}
                          className="text-xs font-medium text-on-surface-variant hover:underline"
                        >
                          Cancel
                        </button>
                        <button
                          disabled={removingMember}
                          onClick={() => {
                            setTeamError('');
                            removeMember({ variables: { projectId: teamProject.id, userId: m.user.id } });
                          }}
                          className={styles.removeConfirmBtn}
                        >
                          {removingMember ? 'Removing…' : 'Confirm remove'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setTeamError(''); setConfirmRemove(m.user.id); }}
                        className={styles.removeBtn}
                        title="Remove from project"
                      >
                        <span className="material-symbols-outlined text-base">person_remove</span>
                        Remove
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )
        )}
      </Modal>
      <Modal
        isOpen={!!ownerProject}
        onClose={closeOwnerDialog}
        title={ownerProject ? `Change owner — ${ownerProject.name}` : ''}
        footer={
          <>
            <button
              onClick={closeOwnerDialog}
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveOwner}
              disabled={!newOwner || changingOwner}
              className="btn-primary px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container disabled:opacity-50"
            >
              {changingOwner ? 'Saving…' : 'Change owner'}
            </button>
          </>
        }
      >
        {ownerProject && (
          <div className="space-y-4">
            <div className={styles.currentOwner}>
              <span className="material-symbols-outlined text-lg text-on-surface-variant">person</span>
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-on-surface-variant">Current owner</p>
                <p className="text-sm text-on-surface">
                  {ownerProject.owner.firstName} {ownerProject.owner.lastName}
                </p>
                <p className="text-xs text-on-surface-variant">
                  {ownerProject.owner.email} · project holds {formatStorage(ownerProject.storageUsed)}
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                New owner
              </label>
              <OwnerPicker
                excludeId={ownerProject.owner.id}
                value={newOwner}
                onChange={(u) => { setNewOwner(u); setOwnerError(''); }}
              />
            </div>

            {newOwner && (
              <p className="text-sm text-on-surface">
                Ownership moves to <span className="font-semibold">{newOwner.firstName} {newOwner.lastName}</span>.
                {keepPrevious
                  ? ` ${ownerProject.owner.firstName} keeps access to the project as an editor.`
                  : ` ${ownerProject.owner.firstName} loses all access to the project.`}
              </p>
            )}

            <label className={styles.overrideRow}>
              <input
                type="checkbox"
                checked={keepPrevious}
                onChange={(e) => setKeepPrevious(e.target.checked)}
              />
              <span>
                Keep {ownerProject.owner.firstName} as a team member
                <span className="block text-xs text-on-surface-variant">
                  They stay on as an editor. Uncheck for a full hand-over — they are removed from
                  the project and lose access to it.
                </span>
              </span>
            </label>

            <label className={styles.overrideRow}>
              <input
                type="checkbox"
                checked={ignoreStorage}
                onChange={(e) => setIgnoreStorage(e.target.checked)}
              />
              <span>
                Ignore the new owner's plan storage limit
                <span className="block text-xs text-on-surface-variant">
                  Without this, the change is refused if the project does not fit their plan.
                </span>
              </span>
            </label>

            {ownerError && <p className={styles.ownerError}>{ownerError}</p>}
          </div>
        )}
      </Modal>
      <Modal
        isOpen={!!logsProject}
        onClose={() => setLogsProject(null)}
        title={logsProject ? `Open logs — ${logsProject.name}` : ''}
        footer={
          <button
            onClick={() => setLogsProject(null)}
            className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
          >
            Close
          </button>
        }
      >
        {logsProject && (
          logsProject.openLogs.length === 0 ? (
            <p className="text-sm text-on-surface-variant py-2">No opens recorded yet.</p>
          ) : (
            <div className={styles.logTable}>
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-surface-container-low">
                    <th className="px-3 py-2 font-semibold text-on-surface-variant">Email</th>
                    <th className="px-3 py-2 font-semibold text-on-surface-variant">IP</th>
                    <th className="px-3 py-2 font-semibold text-on-surface-variant">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {[...logsProject.openLogs].reverse().map((log, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 text-on-surface">{log.email}</td>
                      <td className="px-3 py-2 text-on-surface-variant font-mono">{log.ip || '—'}</td>
                      <td className="px-3 py-2 text-on-surface-variant">
                        {new Date(log.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </Modal>
    </DashboardLayout>
  );
};

export default AdminProjects;
